import { createApiClient } from "#/shared/api/client";

// The server's cap on pairs per request, `PhysiciansController::PROVIDER_ADDRESS_MAX_PAIRS`.
export const PAIRS_PER_REQUEST = 25;

const PAGING_PARAMS = ["page", "per_page"];

/**
 * Creates the reader for result cards of chosen provider/address pairs, `GET /physicians.json` with
 * `provider_address_pairs`.
 *
 * @param {Object} [options]
 * @param {Object} [options.client] the API client; defaults to a raw-JSON `createApiClient`
 */
export function createProviderAddressPairsApi({ client = createApiClient({ parse: false }) } = {}) {
  /**
   * Returns the results for `pairs` under the search `searchUrl` ran, so their order and slots follow its filters.
   * Requests run one after another, `PAIRS_PER_REQUEST` pairs each.
   *
   * @param {String} searchUrl the completed search request URL
   * @param {Array<{physician_id: Number, address_id: Number}>} pairs
   * @param {Object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<Array<Object>>} the results of every batch, in order
   */
  async function results(searchUrl, pairs, { signal } = {}) {
    const path = searchPath(searchUrl);
    const collected = [];

    for (let start = 0; start < pairs.length; start += PAIRS_PER_REQUEST) {
      const params = pairParams(pairs.slice(start, start + PAIRS_PER_REQUEST));
      const payload = await client.request("get", path, { params, signal });

      collected.push(...(payload?.results ?? []));
    }

    return collected;
  }

  return Object.freeze({ results });
}

function searchPath(searchUrl) {
  const url = new URL(searchUrl, window.location.href);

  for (const name of PAGING_PARAMS) {
    url.searchParams.delete(name);
  }

  return url.href;
}

// Rails pairs each `address_id` with the `physician_id` just before it.
function pairParams(pairs) {
  const params = new URLSearchParams();

  for (const pair of pairs) {
    params.append("provider_address_pairs[][physician_id]", pair.physician_id);
    params.append("provider_address_pairs[][address_id]", pair.address_id);
  }

  return params;
}
